/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	forwardRef,
	type Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { bindAll } from 'bind-event-listener';
import { defineMessages, useIntl } from 'react-intl-next';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { ExitingPersistence } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline } from '@atlaskit/primitives/compiled';
import Tooltip from '@atlaskit/tooltip';
import {
	type ConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';

import { useAISmartButtonContext } from '../../../../common/context';
import type { AISmartButtonProps } from '../../../../common/types/shared-types';
import { isBrowser } from '../../../../common/utils/is-browser';
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { AIActionShortcutMenu } from '../../../ai-action-shortcut-menu';
import { AIButton } from '../../../ai-button';
import { AIProactiveNudge } from '../../../ai-proactive-nudge';

const i18n = defineMessages({
	tooltipLabel: {
		id: 'ai-smart-button.tooltip.label',
		defaultMessage: 'Ask Rovo AI',
		description: 'Label for the AI smart button tooltip',
	},
});

const styles = cssMap({
	buttonWrapper: {
		position: 'relative',
	},
});

const EDIT_PAGE_MODE = 'edit';

type AISmartButtonUIProps = Pick<AISmartButtonProps, 'isDisabled'> & {
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
};

export const AISmartButtonUI = forwardRef<HTMLButtonElement, AISmartButtonUIProps>(
	({ onClick, isDisabled, onMouseEnter, onMouseLeave }, ref: Ref<HTMLButtonElement>) => {
		const { sidebarApi, analyticsAttributes, product, productPage, pageMode, getActions } =
			useAISmartButtonContext();
		const { formatMessage } = useIntl();
		const { toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();

		const [canShowNudge, setCanShowNudge] = useState(false);
		const [spotlightRendered, setSpotlightRendered] = useState(false);
		const [hasActiveNudge, setHasActiveNudge] = useState(false);
		const [isHovering, setIsHovering] = useState(false);
		const [actionShortcuts, setActionShortcuts] = useState<ConversationAction[]>([]);
		const placementElementRef = useRef<HTMLDivElement | null>(null);
		const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

		const onboardingSpotlightElementId = useMemo(() => {
			const isLiveOrEditPage =
				analyticsAttributes?.pageMode === 'live' || analyticsAttributes?.pageMode === 'edit';
			const isPageOrBlogpost = productPage === 'page' || productPage === 'blogpost';

			if (isPageOrBlogpost && isLiveOrEditPage) {
				return `rovo-button-onboarding-spotlight-live-or-edit`;
			}

			return `rovo-button-onboarding-spotlight`;
		}, [productPage, analyticsAttributes]);

		// Fetch action shortcuts when experiment is enabled
		const slaOperationsEnabled = fg('jmb-4989_add_sla_operations_in_rovo');
		useEffect(() => {
			const confluenceShortcutsEnabled =
				FeatureGates.getExperimentValue<boolean>(
					'confluence_rovo_button_shortcuts_editor_only',
					'isEnabled',
					false,
				) === true;
			const shouldFetch = getActions && (confluenceShortcutsEnabled || product === 'jira');

			if (shouldFetch) {
				getActions()
					.then((actions) => {
						// Filter to only get actions that can be used as shortcuts (Base, Chat, or Skill e.g. Jira SLA when feature-flagged)
						const shortcuts = actions.filter(
							(action) =>
								action.handleType === HandleActionType.Base ||
								action.handleType === HandleActionType.Chat ||
								(slaOperationsEnabled && action.handleType === HandleActionType.Skill),
						);
						const slicedShortcuts = shortcuts.slice(0, 3);
						setActionShortcuts(slicedShortcuts);
					})
					.catch(() => {
						setActionShortcuts([]);
					});
			}
		}, [getActions, product, slaOperationsEnabled]);

		// Handle "Get Started" button click from spotlight
		const handleGetStartedClick = useCallback(() => {
			if (sidebarApi) {
				toggleConversationAssistantUIMode({
					targetMode: 'mini-modal',
					sidebarApi,
				});
			}
		}, [sidebarApi, toggleConversationAssistantUIMode]);

		// Set up event listeners for spotlight communication
		useEffect(() => {
			if (!isBrowser() || !placementElementRef.current || !fg('rovo_button_onboarding_spotlight')) {
				return;
			}

			const doc = getDocument();

			if (!doc) {
				return;
			}

			// Listen for spotlight rendered event
			const handleSpotlightRendered = (event: Event) => {
				const customEvent = event as CustomEvent;
				if (customEvent.detail?.elementId === onboardingSpotlightElementId) {
					setSpotlightRendered(true);
				}
			};

			// Listen for "Get Started" button click event
			const handleGetStartedEvent = (event: Event) => {
				const customEvent = event as CustomEvent;
				if (customEvent.detail?.elementId === onboardingSpotlightElementId) {
					handleGetStartedClick();
				}
			};

			const unbind = bindAll(doc, [
				{
					type: 'rovo-button-onboarding-spotlight-rendered',
					listener: handleSpotlightRendered,
				},
				{
					type: 'rovo-button-onboarding-spotlight-get-started-click',
					listener: handleGetStartedEvent,
				},
			]);

			return () => {
				unbind();
			};
		}, [handleGetStartedClick, onboardingSpotlightElementId]);

		useEffect(() => {
			if (spotlightRendered) {
				return;
			}

			if (!fg('rovo_button_onboarding_spotlight') && !canShowNudge) {
				setCanShowNudge(true);
				return;
			}

			// Give spotlight time to render (if it will) since it needs to run decisions logic
			const timeout = setTimeout(() => {
				if (!spotlightRendered && !canShowNudge) {
					setCanShowNudge(true);
				}
			}, 1000);

			return () => clearTimeout(timeout);
		}, [spotlightRendered, canShowNudge]);

		const nudgeOrPlacement = useMemo(() => {
			return (
				<React.Fragment>
					{fg('rovo_button_onboarding_spotlight') && (
						<div id={onboardingSpotlightElementId} ref={placementElementRef} />
					)}
					{canShowNudge && <AIProactiveNudge onActiveNudgeChange={setHasActiveNudge} />}
				</React.Fragment>
			);
		}, [canShowNudge, onboardingSpotlightElementId]);

		const handleButtonMouseEnter = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHovering(true);
			}, 250);
			onMouseEnter();
		}, [onMouseEnter]);

		const handleButtonMouseLeave = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHovering(false);
				onMouseLeave();
			}, 1000);
		}, [onMouseLeave]);

		const handleActionBoxMouseEnter = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			setIsHovering(true);
		}, []);

		const handleActionBoxMouseLeave = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHovering(false);
			}, 1000);
		}, []);

		const canShowActionShortcuts = useMemo(() => {
			const confluenceShortcutsEnabled =
				FeatureGates.getExperimentValue<boolean>(
					'confluence_rovo_button_shortcuts_editor_only',
					'isEnabled',
					false,
				) === true;
			const confluenceEditMode =
				pageMode?.toLowerCase() === EDIT_PAGE_MODE && confluenceShortcutsEnabled;
			const jiraWithShortcuts =
				slaOperationsEnabled && product === 'jira' && actionShortcuts.length > 0;
			return (
				(actionShortcuts.length > 0 &&
					!hasActiveNudge &&
					!spotlightRendered &&
					(confluenceEditMode || jiraWithShortcuts)) === true
			);
		}, [
			actionShortcuts.length,
			hasActiveNudge,
			spotlightRendered,
			pageMode,
			product,
			slaOperationsEnabled,
		]);

		const actionShortcutsRendering = useMemo(() => {
			if (!isHovering || !canShowActionShortcuts) {
				return null;
			}

			return (
				<AIActionShortcutMenu
					actionShortcuts={actionShortcuts}
					onMouseEnter={handleActionBoxMouseEnter}
					onMouseLeave={handleActionBoxMouseLeave}
				/>
			);
		}, [
			isHovering,
			canShowActionShortcuts,
			actionShortcuts,
			handleActionBoxMouseEnter,
			handleActionBoxMouseLeave,
		]);

		return (
			<Inline space="space.100" alignBlock="center" xcss={styles.buttonWrapper}>
				{nudgeOrPlacement}
				<Tooltip
					position="left"
					content={formatMessage(i18n.tooltipLabel)}
					hideTooltipOnClick
					ignoreTooltipPointerEvents
					canAppear={() => {
						// If control or not on editor surface, always show tooltip when no active nudge
						// If treatment and on editor surface, show tooltip only when shortcuts cannot be shown
						return !hasActiveNudge && !canShowActionShortcuts;
					}}
				>
					<AIButton
						onClick={onClick}
						isDisabled={isDisabled}
						onMouseEnter={handleButtonMouseEnter}
						onMouseLeave={handleButtonMouseLeave}
						ref={ref}
					/>
				</Tooltip>
				<ExitingPersistence appear>{actionShortcutsRendering}</ExitingPersistence>
			</Inline>
		);
	},
);
